import { useCallback, useState } from 'react';
import { Crosshair, X, Loader2, RefreshCw, Activity } from 'lucide-react';
import { useAccountStore } from '../../stores/accountStore';
import { usePositionPnl } from '../../hooks/ui/usePositionPnl';
import { ActivityTicker } from '../../components/ActivityTicker';

// ── Helpers ─────────────────────────────────────────────────────

function truncateTicker(ticker: string): string {
  if (ticker.length <= 24) return ticker;
  const parts = ticker.split('-');
  if (parts.length >= 2) return `...${parts.slice(-2).join('-')}`;
  return `...${ticker.slice(-20)}`;
}

function formatCents(cents: number): string {
  return `${cents}\u00A2`;
}

function formatDollars(cents: number): string {
  const abs = Math.abs(cents);
  const sign = cents >= 0 ? '+' : '-';
  return `${sign}$${(abs / 100).toFixed(2)}`;
}

// ── Position Tracker Screen ─────────────────────────────────────

export function PositionTracker() {
  const status = useAccountStore((s) => s.status);
  const balance = useAccountStore((s) => s.balance);
  const positions = useAccountStore((s) => s.positions);
  const openOrders = useAccountStore((s) => s.openOrders);
  const cancelOrder = useAccountStore((s) => s.cancelOrder);
  const refreshPositions = useAccountStore((s) => s.refreshPositions);
  const refreshOrders = useAccountStore((s) => s.refreshOrders);
  const placeOrder = useAccountStore((s) => s.placeOrder);

  const { positionsWithPnl, totalPnl } = usePositionPnl();

  const [closingTicker, setClosingTicker] = useState<string | null>(null);
  const [cancelingOrder, setCancelingOrder] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const handleClosePosition = useCallback(async (ticker: string, side: 'yes' | 'no', qty: number) => {
    setClosingTicker(ticker);
    try {
      await placeOrder({
        ticker,
        side,
        action: 'sell',
        type: 'market',
        count: qty,
      });
    } catch (err) {
      console.error('Close position failed:', err);
    } finally {
      setClosingTicker(null);
    }
  }, [placeOrder]);

  const handleCancelOrder = useCallback(async (orderId: string) => {
    setCancelingOrder(orderId);
    try {
      await cancelOrder(orderId);
    } catch (err) {
      console.error('Cancel order failed:', err);
    } finally {
      setCancelingOrder(null);
    }
  }, [cancelOrder]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([refreshPositions(), refreshOrders()]);
    setRefreshing(false);
  }, [refreshPositions, refreshOrders]);

  // ── Disconnected state ──────────────────────────────────────
  if (status !== 'connected') {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-gray-500">
        <Crosshair size={48} strokeWidth={1} />
        <h2 className="text-lg font-medium text-gray-300">Position Tracker</h2>
        <p className="text-sm">Connect your Kalshi account in Settings to track positions</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-6">
      <div className="mx-auto flex max-w-7xl gap-6">
      {/* Main content */}
      <div className="min-w-0 flex-1 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Crosshair size={20} className="text-gray-400" />
            <h1 className="text-xl font-semibold">Positions</h1>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="rounded p-1.5 text-gray-500 transition-colors hover:bg-gray-800 hover:text-gray-300"
            >
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            </button>
            {balance !== null && (
              <span className="text-sm text-gray-400">
                Balance: <span className="font-medium text-gray-200">${(balance / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </span>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-lg border border-gray-800 bg-gray-900/50 px-4 py-3">
            <div className="text-xs text-gray-500">Unrealized P&L</div>
            <div className={`mt-1 text-lg font-semibold ${totalPnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {formatDollars(totalPnl)}
            </div>
          </div>
          <div className="rounded-lg border border-gray-800 bg-gray-900/50 px-4 py-3">
            <div className="text-xs text-gray-500">Positions</div>
            <div className="mt-1 text-lg font-semibold text-gray-200">{positions.length}</div>
          </div>
          <div className="rounded-lg border border-gray-800 bg-gray-900/50 px-4 py-3">
            <div className="text-xs text-gray-500">Open Orders</div>
            <div className="mt-1 text-lg font-semibold text-gray-200">{openOrders.length}</div>
          </div>
        </div>

        {/* Positions Table */}
        <div className="rounded-lg border border-gray-800 bg-gray-900/50">
          <div className="border-b border-gray-800 px-4 py-2.5">
            <h2 className="text-sm font-medium text-gray-300">Open Positions</h2>
          </div>
          {positionsWithPnl.length > 0 ? (
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-2 text-left">Market</th>
                  <th className="px-3 py-2 text-center">Side</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-right">Avg</th>
                  <th className="px-3 py-2 text-right">Current</th>
                  <th className="px-3 py-2 text-right">P&L</th>
                  <th className="w-10 px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {positionsWithPnl.map((pos) => (
                  <tr key={`${pos.ticker}-${pos.side}`} className="border-b border-gray-800/40 hover:bg-gray-800/30">
                    <td className="px-4 py-2 font-mono text-xs text-gray-300">{truncateTicker(pos.ticker)}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                        pos.side === 'yes' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {pos.side.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-xs text-gray-300">{pos.position}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs text-gray-400">{formatCents(pos.averagePrice)}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs text-gray-300">
                      {pos.currentPrice !== null ? formatCents(pos.currentPrice) : '\u2014'}
                    </td>
                    <td className={`px-3 py-2 text-right font-mono text-xs font-medium ${
                      pos.unrealizedPnl === null ? 'text-gray-500' :
                      pos.unrealizedPnl >= 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {pos.unrealizedPnl !== null ? formatDollars(pos.unrealizedPnl) : '\u2014'}
                    </td>
                    <td className="px-2 py-2">
                      <button
                        onClick={() => handleClosePosition(pos.ticker, pos.side, pos.position)}
                        disabled={closingTicker === pos.ticker}
                        className="rounded p-1 text-gray-600 transition-colors hover:bg-red-500/10 hover:text-red-400 disabled:opacity-50"
                        title="Close position (market sell)"
                      >
                        {closingTicker === pos.ticker ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="px-4 py-8 text-center text-sm text-gray-500">No open positions</div>
          )}
        </div>

        {/* Open Orders Table */}
        <div className="rounded-lg border border-gray-800 bg-gray-900/50">
          <div className="border-b border-gray-800 px-4 py-2.5">
            <h2 className="text-sm font-medium text-gray-300">Open Orders</h2>
          </div>
          {openOrders.length > 0 ? (
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-2 text-left">Market</th>
                  <th className="px-3 py-2 text-center">Side</th>
                  <th className="px-3 py-2 text-center">Type</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-right">Price</th>
                  <th className="px-3 py-2 text-center">Status</th>
                  <th className="w-10 px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {openOrders.map((order) => (
                  <tr key={order.orderId} className="border-b border-gray-800/40 hover:bg-gray-800/30">
                    <td className="px-4 py-2 font-mono text-xs text-gray-300">{truncateTicker(order.ticker)}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                        order.side === 'yes' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {order.side.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center text-xs text-gray-400 capitalize">{order.type}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs text-gray-300">{order.remainingCount}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs text-gray-300">
                      {order.yesPrice ? formatCents(order.yesPrice) : order.noPrice ? formatCents(order.noPrice) : '\u2014'}
                    </td>
                    <td className="px-3 py-2 text-center text-xs text-gray-500 capitalize">{order.status}</td>
                    <td className="px-2 py-2">
                      <button
                        onClick={() => handleCancelOrder(order.orderId)}
                        disabled={cancelingOrder === order.orderId}
                        className="rounded p-1 text-gray-600 transition-colors hover:bg-red-500/10 hover:text-red-400 disabled:opacity-50"
                        title="Cancel order"
                      >
                        {cancelingOrder === order.orderId ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="px-4 py-8 text-center text-sm text-gray-500">No open orders</div>
          )}
        </div>
      </div>

      {/* Activity Ticker sidebar */}
      <div className="hidden w-72 shrink-0 lg:block">
        <div className="sticky top-6">
          <div className="rounded-lg border border-gray-800 bg-gray-900/50">
            <div className="flex items-center gap-2 border-b border-gray-800 px-4 py-2.5">
              <Activity size={14} className="text-gray-400" />
              <h2 className="text-sm font-medium text-gray-300">Recent Fills</h2>
            </div>
            <div className="max-h-[calc(100vh-12rem)] overflow-y-auto py-1">
              <ActivityTicker />
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
